import { Container, Typography, Box, Paper, Stack } from "@mui/material";
import {
    QueryObserverResult,
    RefetchOptions,
    RefetchQueryFilters,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import {
    DocumentInfo,
    DocumentSection,
} from "../../../../../../models/documents";
import { TReadDocumentsDataResponse } from "../../../../../../services/budget/read-budget-documents";
import { TReadBudgetsResponse } from "../../../../../../services/budget/read-budgets";
import { TReadBudgetsLevelsDetailsByIdResponse } from "../../../../../../services/budget/read-budgets-levels-detailed-by-id";
import AddSectionForm from "./AddSectionForm";
import DocumentHeader from "./components/DocumentHeader";
import DocumentPreview from "./components/DocumentPreview";
import { TemplateManager } from "./components/templates";
import { useDocumentsRequest } from "./hook/useDocument";
import SectionItem from "./SectionItem";

type TDocumentContent = {
    budgetData: TReadBudgetsResponse;
    documentsData: TReadDocumentsDataResponse;
    levelData: TReadBudgetsLevelsDetailsByIdResponse;
    refetchDocuments: <TPageData>(
        options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
    ) => Promise<QueryObserverResult<TReadDocumentsDataResponse, unknown>>;
};

const DocumentContent = ({
    budgetData,
    documentsData,
    levelData,
    refetchDocuments,
}: TDocumentContent) => {
    const [documentInfo, setDocumentInfo] = useState<
        DocumentInfo & Partial<{ documentId?: string; date: string }>
    >({
        document_title: "DPP - Documento de Proposição de Projeto",
        enterprise: budgetData.client.name,
        project: budgetData.name,
        date: new Date().toISOString().split("T")[0],
    });
    const {
        handleCreateSection,
        handleUpdateSection: handleUpdateSectionRequest,
        handleOrderSection,
        handleUpdateDocument,
        handleCreateDocument,
        handleDeleteDocumentSection,
    } = useDocumentsRequest();

    const sections = documentInfo.documentId
        ? documentsData.find(
              (documentObject) =>
                  documentObject.document.id_e === documentInfo.documentId
          )?.session ?? []
        : [];

    const handleSaveDocumentInfoAfterCreation = (resp: {
        data: {
            id_e: string;
        };
    }) => {
        refetchDocuments();
        setDocumentInfo((prev) => ({ ...prev, documentId: resp.data.id_e }));
    };

    const handleSaveDocumentInfoAfterCreationToSection = async (
        resp: {
            data: {
                id_e: string;
            };
        },
        sectionData: Omit<DocumentSection, "documentId">
    ) => {
        refetchDocuments();
        setDocumentInfo((prev) => ({ ...prev, documentId: resp.data.id_e }));
        const { type, content, position, session_title } = sectionData;
        const params = { type, content, position, session_title };
        const newSection = {
            documentId: resp.data.id_e,
            params,
        };
        await handleCreateSection(newSection);
        await refetchDocuments();
    };

    const handleUpdateInfo = (info: DocumentInfo) => {
        setDocumentInfo((prev) => ({ ...info, documentId: prev.documentId }));
        return setDocumentInfo((prev) => ({
            ...prev,
            date: new Date().toISOString().split("T")[0],
            document_title: info.document_title,
            enterprise: info.enterprise,
            project: info.project,
        }));
    };

    const handleSaveDocument = useCallback(async () => {
        const params = {
            document_title: documentInfo.document_title,
            enterprise: documentInfo.enterprise,
            project: documentInfo.project,
        };
        if (documentInfo.documentId) {
            const params = {
                document_title: documentInfo.document_title,
                enterprise: documentInfo.enterprise,
                project: documentInfo.project,
            };
            return handleUpdateDocument(
                {
                    documentId: documentInfo.documentId,
                    params,
                },
                refetchDocuments
            );
        }
        return handleCreateDocument(
            { budgetId: budgetData.id_e, params },
            handleSaveDocumentInfoAfterCreation
        );
    }, [documentInfo]);

    const handleApplyTemplate = (documentId: string) => {
        const findDocumentById = documentsData.find(
            (document) => document.document.id_e === documentId
        );

        if (findDocumentById) {
            setDocumentInfo({
                ...findDocumentById?.document,
                documentId: findDocumentById?.document.id_e,
            });
        }
    };

    const handleAddSection = (
        sectionData: Omit<DocumentSection, "documentId">
    ) => {
        if (!documentInfo.documentId) {
            const params = {
                document_title: documentInfo.document_title,
                enterprise: documentInfo.enterprise,
                project: documentInfo.project,
            };
            return handleCreateDocument(
                {
                    budgetId: budgetData.id_e,
                    params,
                },
                (resp: {
                    data: {
                        id_e: string;
                    };
                }) =>
                    handleSaveDocumentInfoAfterCreationToSection(
                        resp,
                        sectionData
                    )
            );
        }
        const { content, position, session_title, type } = sectionData;
        const newSection = {
            documentId: documentInfo.documentId,
            params: { content, position, session_title, type },
        };
        return handleCreateSection(newSection, refetchDocuments);
    };

    const handleUpdateSection = async (
        id: string,
        updates: Partial<DocumentSection>
    ) => {
        const { session_title, content } = updates;
        const updatedSections = {
            sectionId: id,
            params: { session_title, content },
        };
        return handleUpdateSectionRequest(updatedSections, refetchDocuments);
    };

    const handleDeleteSection = async (id: string) => {
        const sectionToDelete = sections.find((section) => section.id_e === id);
        if (!sectionToDelete) return;

        const deletedPosition = sectionToDelete.position;

        const sectionsToUpdate = sections
            .filter((section) => section.position > deletedPosition)
            .map((section) => ({
                sectionId: section.id_e,
                params: {
                    position: section.position - 1,
                },
            }));

        await Promise.all(
            sectionsToUpdate.map((section) => handleOrderSection(section))
        );

        await handleDeleteDocumentSection({
            documentId: documentInfo.documentId!,
            sessionId: id,
        });

        toast.success("Seção removida com sucesso.");
        refetchDocuments();
    };
    const handleMoveSection = async (id: string, direction: "up" | "down") => {
        const currentSection = sections.find((section) => section.id_e === id);
        if (!currentSection) return;

        const currentPosition = currentSection.position;

        const swapWithSection = sections.find((section) =>
            direction === "up"
                ? section.position === currentPosition - 1
                : section.position === currentPosition + 1
        );

        // Impede movimentação fora dos limites
        if (!swapWithSection) return;

        const updatedSections = [
            {
                sectionId: currentSection.id_e,
                params: {
                    position: swapWithSection.position,
                },
            },
            {
                sectionId: swapWithSection.id_e,
                params: {
                    position: currentSection.position,
                },
            },
        ];

        await Promise.all(
            updatedSections.map((section) => handleOrderSection(section))
        ).finally(() => refetchDocuments());
    };

    const sortedSections = [...sections].sort(
        (a, b) => a.position - b.position
    );
    const nextOrder =
        sections.length > 0
            ? Math.max(...sections.map((s) => s.position)) + 1
            : 1;

    const tableScopeData = levelData;
    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box sx={{ mb: 4 }}>
                <TemplateManager
                    documentsData={documentsData}
                    documentInfo={documentInfo}
                    refetchDocuments={refetchDocuments}
                    onApplyTemplate={handleApplyTemplate}
                    onSaveDocument={handleSaveDocument}
                />
            </Box>
            <DocumentHeader
                documentInfo={documentInfo}
                handleUpdateInfo={handleUpdateInfo}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                }}
            >
                <Typography variant="h5">Seções do Documento</Typography>
                <DocumentPreview
                    sections={sortedSections}
                    documentInfo={documentInfo}
                    tableScopeData={tableScopeData}
                    budgetData={budgetData}
                />
            </Box>

            <AddSectionForm
                onAddSection={handleAddSection}
                nextOrder={nextOrder}
            />

            {sortedSections.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        textAlign: "center",
                        py: 6,
                        bgcolor: "background.default",
                        border: "1px dashed rgba(0, 0, 0, 0.2)",
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Nenhuma seção criada
                    </Typography>
                    <Typography color="text.secondary">
                        Clique em "Adicionar Nova Seção" para começar a
                        construir seu documento.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {sortedSections.map((section, index) => (
                        <SectionItem
                            key={section.id_e}
                            section={section}
                            onUpdate={handleUpdateSection}
                            onDelete={handleDeleteSection}
                            onMoveUp={(id) => handleMoveSection(id, "up")}
                            onMoveDown={(id) => handleMoveSection(id, "down")}
                            isFirst={index === 0}
                            isLast={index === sortedSections.length - 1}
                            tableScopeData={tableScopeData}
                            budgetData={budgetData}
                        />
                    ))}
                </Stack>
            )}
        </Container>
    );
};

export default DocumentContent;
